import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_POSITION = 50;
const DEFAULT_ASPECT_RATIO = 1100 / 460;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizePosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_POSITION;
  }

  return clamp(parsed, 0, 100);
}

export default function ProfileCoverPositionModal({
  open,
  file,
  frameAspectRatio = DEFAULT_ASPECT_RATIO,
  isSaving = false,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation();
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    baseOffsetX: 0,
    baseOffsetY: 0,
  });

  const [src, setSrc] = useState("");
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: DEFAULT_POSITION,
    y: DEFAULT_POSITION,
  });
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!open || !file) return undefined;

    const url = URL.createObjectURL(file);
    setSrc(url);
    setPosition({ x: DEFAULT_POSITION, y: DEFAULT_POSITION });
    setZoom(1);
    setNaturalSize({ width: 0, height: 0 });

    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  useEffect(() => {
    if (!open) return undefined;

    function measureFrame() {
      const rect = frameRef.current?.getBoundingClientRect();
      if (!rect) return;

      setFrameSize({
        width: rect.width,
        height: rect.height,
      });
    }

    measureFrame();

    if (!frameRef.current) return undefined;

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measureFrame);
      observer.observe(frameRef.current);
    } else {
      window.addEventListener("resize", measureFrame);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureFrame);
    };
  }, [open]);

  const baseCoverScale = useMemo(() => {
    if (!frameSize.width || !frameSize.height || !naturalSize.width || !naturalSize.height) {
      return 1;
    }

    return Math.max(
      frameSize.width / naturalSize.width,
      frameSize.height / naturalSize.height
    );
  }, [frameSize.height, frameSize.width, naturalSize.height, naturalSize.width]);

  const coverScale = baseCoverScale * zoom;

  const maxOffsets = useMemo(() => {
    const imageWidth = naturalSize.width * coverScale;
    const imageHeight = naturalSize.height * coverScale;

    return {
      x: Math.max(0, (imageWidth - frameSize.width) / 2),
      y: Math.max(0, (imageHeight - frameSize.height) / 2),
    };
  }, [coverScale, frameSize.height, frameSize.width, naturalSize.height, naturalSize.width]);

  const offset = useMemo(() => {
    return {
      x: maxOffsets.x
        ? ((position.x - DEFAULT_POSITION) / DEFAULT_POSITION) * maxOffsets.x
        : 0,
      y: maxOffsets.y
        ? ((position.y - DEFAULT_POSITION) / DEFAULT_POSITION) * maxOffsets.y
        : 0,
    };
  }, [maxOffsets.x, maxOffsets.y, position.x, position.y]);

  function clampOffsets(nextOffsetX, nextOffsetY) {
    return {
      x: clamp(nextOffsetX, -maxOffsets.x, maxOffsets.x),
      y: clamp(nextOffsetY, -maxOffsets.y, maxOffsets.y),
    };
  }

  function offsetToPosition(nextOffsetX, nextOffsetY) {
    return {
      x: maxOffsets.x
        ? clamp(
            DEFAULT_POSITION + (nextOffsetX / maxOffsets.x) * DEFAULT_POSITION,
            0,
            100
          )
        : DEFAULT_POSITION,
      y: maxOffsets.y
        ? clamp(
            DEFAULT_POSITION + (nextOffsetY / maxOffsets.y) * DEFAULT_POSITION,
            0,
            100
          )
        : DEFAULT_POSITION,
    };
  }

  function handlePointerDown(event) {
    if (!src || !naturalSize.width || !naturalSize.height) {
      return;
    }

    event.preventDefault();
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseOffsetX: offset.x,
      baseOffsetY: offset.y,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function handlePointerMove(event) {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    const nextOffsets = clampOffsets(
      dragRef.current.baseOffsetX + deltaX,
      dragRef.current.baseOffsetY + deltaY
    );

    setPosition(offsetToPosition(nextOffsets.x, nextOffsets.y));
  }

  function handlePointerEnd(event) {
    if (dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    dragRef.current.active = false;
    dragRef.current.pointerId = null;
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }

  function handleImageLoad() {
    const img = imgRef.current;
    if (!img) return;

    setNaturalSize({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  }

  if (!open || !file) return null;

  return (
    <div
      className="profile-cover-position-overlay"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="profile-cover-position-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="profile-cover-position-title">{t("adjust_cover")}</div>

        <div
          ref={frameRef}
          className={`profile-cover-position-frame${isDragging ? " is-dragging" : ""}`}
          style={{ aspectRatio: String(frameAspectRatio || DEFAULT_ASPECT_RATIO) }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        >
          {src ? (
            <img
              ref={imgRef}
              src={src}
              alt={t("post_media")}
              className="profile-cover-position-image"
              onLoad={handleImageLoad}
              draggable={false}
              style={{
                transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${coverScale})`,
              }}
            />
          ) : null}
        </div>

        <div className="profile-cover-position-zoom">
          <label htmlFor="cover-zoom-slider">
            {t("zoom", { defaultValue: "Zoom" })}
          </label>
          <input
            id="cover-zoom-slider"
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="profile-cover-zoom-slider"
          />
        </div>

        <div className="profile-cover-position-hint">
          {t("drag_to_reposition")}
        </div>

        <div className="profile-cover-position-actions">
          <button
            className="close-popup-btn"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t("cancel")}
          </button>
          <button
            className="popup-add-photo-btn"
            disabled={isSaving}
            onClick={() =>
              onConfirm({
                file,
                coverPositionX: normalizePosition(position.x),
                coverPositionY: normalizePosition(position.y),
                coverScale: zoom,
              })
            }
          >
            {isSaving
              ? t("saving", { defaultValue: "Saving..." })
              : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
}
